import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';

type DiffRow = {
  type: 'equal' | 'add' | 'del';
  left?: string;
  right?: string;
};

type DiffRowWithLine = DiffRow & {
  leftNo?: number | null;
  rightNo?: number | null;
};

const splitLines = (text: string) => {
  return text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
};

const buildLineDiff = (oldText: string, newText: string): DiffRow[] => {
  const a = splitLines(oldText || '');
  const b = splitLines(newText || '');
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));

  for (let i = m - 1; i >= 0; i -= 1) {
    for (let j = n - 1; j >= 0; j -= 1) {
      if (a[i] === b[j]) {
        dp[i][j] = dp[i + 1][j + 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
      }
    }
  }

  const rows: DiffRow[] = [];
  let i = 0;
  let j = 0;

  while (i < m && j < n) {
    if (a[i] === b[j]) {
      rows.push({ type: 'equal', left: a[i], right: b[j] });
      i += 1;
      j += 1;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      rows.push({ type: 'del', left: a[i] });
      i += 1;
    } else {
      rows.push({ type: 'add', right: b[j] });
      j += 1;
    }
  }

  while (i < m) {
    rows.push({ type: 'del', left: a[i] });
    i += 1;
  }

  while (j < n) {
    rows.push({ type: 'add', right: b[j] });
    j += 1;
  }

  return rows;
};

type ProfileDiffViewProps = {
  originalText: string;
  suggestedText: string;
};

export const ProfileDiffView: React.FC<ProfileDiffViewProps> = ({ originalText, suggestedText }) => {
  const rows = useMemo(() => buildLineDiff(originalText, suggestedText), [originalText, suggestedText]);
  const rowsWithNumbers = useMemo<DiffRowWithLine[]>(() => {
    let left = 1;
    let right = 1;
    return rows.map((row) => {
      const next: DiffRowWithLine = {
        ...row,
        leftNo: row.left !== undefined ? left : null,
        rightNo: row.right !== undefined ? right : null,
      };
      if (row.left !== undefined) left += 1;
      if (row.right !== undefined) right += 1;
      return next;
    });
  }, [rows]);

  return (
    <Box
      sx={{
        border: '1px solid #1f242f',
        borderRadius: 2,
        backgroundColor: '#0f1116',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          borderBottom: '1px solid #1f242f',
        }}
      >
        <Box sx={{ p: 1.5, borderRight: '1px solid #1f242f' }}>
          <Typography variant="caption" color="text.secondary">
            Aktuelles Profil
          </Typography>
        </Box>
        <Box sx={{ p: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            Vorschlag
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          maxHeight: 360,
          overflow: 'auto',
          fontFamily: '"JetBrains Mono", "Fira Code", monospace',
          fontSize: 12,
        }}
      >
        {rowsWithNumbers.map((row, index) => {
          const leftBg = row.type === 'del' ? 'rgba(248, 113, 113, 0.12)' : 'transparent';
          const rightBg = row.type === 'add' ? 'rgba(16, 185, 129, 0.12)' : 'transparent';
          const leftColor = row.type === 'del' ? '#fca5a5' : '#e5e7eb';
          const rightColor = row.type === 'add' ? '#86efac' : '#e5e7eb';

          return (
            <React.Fragment key={`diff-${index}`}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr',
                  gap: 1,
                  px: 1.5,
                  py: 0.5,
                  borderRight: '1px solid #1f242f',
                  backgroundColor: leftBg,
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 28 }}>
                  {row.leftNo ?? ''}
                </Typography>
                <Typography variant="caption" sx={{ color: leftColor, whiteSpace: 'pre-wrap' }}>
                  {row.left ?? ''}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr',
                  gap: 1,
                  px: 1.5,
                  py: 0.5,
                  backgroundColor: rightBg,
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 28 }}>
                  {row.rightNo ?? ''}
                </Typography>
                <Typography variant="caption" sx={{ color: rightColor, whiteSpace: 'pre-wrap' }}>
                  {row.right ?? ''}
                </Typography>
              </Box>
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
};
